import React, { useEffect, useState } from "react"
import { Badge, Button, Card, Collapse, Stack, Table } from "react-bootstrap"
import InfiniteScroll from "react-infinite-scroll-component"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"
import recordNotFound from "../../../assets/images/record_not_found.png"
import PageTitleButton from "../../../components/PageTitleButton"
import SearchBox from "../../../components/SearchBox"
import SweetAlerts from "../../../components/SweetAlerts"
import TooltipComponent from "../../../components/Tooltip"
import { admin } from "../../../config/apiEndpoints"
import axiosProvider from "../../../lib/AxiosProvider"
import {
  useHandleUnauthorizedAccess,
  HasModulePermission,
} from "../../../lib/GlobalMethods"
import useDebounce from "../../../lib/hooks/useDebounce"
import ManagePermissionsSkeleton from "../../../skeleton/settings/ManagePermissionsSkeleton"
import AddEditUser from "./AddEditUser"
import { modulePermission } from "../../../lib/GlobalVariables"
import { useSelector } from "react-redux"

const ManageUsers = () => {
  const canEdit = HasModulePermission(modulePermission.setting, "edit")
  const canDelete = HasModulePermission(modulePermission.setting, "delete")
  const canAdd = HasModulePermission(modulePermission.setting, "add")

  const [show, setShow] = useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [data, setData] = useState(null)
  const [isUpdate, setIsUpdate] = useState(false)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: false,
  })
  const [recordsCount, setRecordsCount] = useState({
    total_records: false,
    active_records: false,
    archived_records: false,
  })
  const [searchQuery, setSearchQuery] = useState("")
  const [hasMore, setHasMore] = useState(true)
  const [filterObj, setFilterObj] = useState({
    archived: false,
    active: false,
  })
  const { userDetails } = useSelector((state) => state.basicInformation)

  const [archived, setArchived] = useState(true)

  const debouncedSearchQuery = useDebounce(searchQuery, 500)

  const { handleAccess } = useHandleUnauthorizedAccess()

  const handleClose = () => {
    setShow(false)
    setIsUpdate(false)
    setShowAlert(false)
  }

  const handleShow = () => setShow(true)

  const handleShowAlert = () => {
    setShowAlert(true)
  }

  const fetchData = async (searchQuery) => {
    try {
      const response = await axiosProvider({
        method: "GET",
        endpoint: `${admin.adminUsersList}?page=${pagination.current_page}&search_term=${searchQuery}&archived=${filterObj.archived}&limit=20&active=${filterObj.active}`,
      })

      const fetchedData = response?.data?.data || []
      setHasMore(true)

      handleAccess({ code: response?.data?.code })

      response?.data?.code === 200 && setPagination(response?.data?.pagination)
      response?.data?.code === 200 &&
        setRecordsCount({
          total_records: response?.data?.record_counts?.total_records - 1,
          active_records: response?.data?.record_counts?.active_records - 1,
          archived_records: response?.data?.record_counts?.archived_records,
        })

      if (
        !fetchedData.length ||
        response.data.pagination.current_page ===
          response.data.pagination.total_pages
      ) {
        setHasMore(false)
      }

      if (userDetails?.id) {
        const filteredData = fetchedData.filter(
          (user) => user.id !== userDetails.id,
        )
        setData((prevData) =>
          pagination.current_page === 1
            ? filteredData
            : [...prevData, ...filteredData],
        )
      } else {
        setData((prevData) =>
          pagination.current_page === 1
            ? fetchedData
            : [...prevData, ...fetchedData],
        )
      }

      setLoading(false)
    } catch (error) {
      setLoading(false)
      toast.error(error.message || "An error occurred")
    }
  }

  const fetchMoreData = () => {
    if (pagination.current_page < pagination.total_pages) {
      setPagination((prevState) => ({
        ...prevState,
        current_page: prevState.current_page + 1,
      }))
    } else {
      setHasMore(false)
    }
  }

  const handleArchive = async (id) => {
    try {
      const response = await axiosProvider({
        method: "DELETE",
        endpoint: archived
          ? `${admin.restoreAdminUser + id}`
          : `${admin.deleteAdminUser + id}`,
      })
      if (response?.data?.code === 200) {
        toast.success(response.data.message)

        setData((data) => {
          if (filterObj.archived === false && filterObj.active === false) {
            return data.map((user) => {
              if (user.id === id) {
                user.deletedAt = archived ? null : Date.now()
              }
              return user
            })
          } else {
            return data.filter((user) => user.id !== id)
          }
        })

        if (archived) {
          setRecordsCount((prev) => ({
            ...prev,
            active_records: prev.active_records + 1,
            archived_records: prev.archived_records - 1,
          }))
        } else {
          setRecordsCount((prev) => ({
            ...prev,
            active_records: prev.active_records - 1,
            archived_records: prev.archived_records + 1,
          }))
        }
        setLoading(false)
      } else {
        setLoading(false)
        toast.error(response.data.message)
      }
    } catch (error) {
      setLoading(false)
      toast.error("Failed to delete user")
    }
  }

  useEffect(() => {
    fetchData(debouncedSearchQuery)
  }, [debouncedSearchQuery, filterObj, pagination.current_page])

  const filterOnClick = () => {
    setOpen(!open)
  }

  return (
    <>
      {canAdd ? (
        <PageTitleButton
          PagetitleText={"Manage Users"}
          HeaderButtonText={"Create User"}
          IconclassName={"plus_black"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      ) : (
        <PageTitleButton
          PagetitleText={"Manage Users"}
          onClick={handleShow}
          filterShow={true}
          filterClick={filterOnClick}
          open={open}
        />
      )}

      <div className="details_cards_wrapper mb-3">
        <div
          className={`bg_sea_green details_card ${
            !filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ active: false, archived: false })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Total Users</h3>
            <span className="card_icon">
              <i className="icon_main chargin_stations"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.total_records}</p>
        </div>
        <div
          className={`bg_light_green details_card ${
            filterObj.active && !filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ active: true, archived: false })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Active Users</h3>
            <span className="card_icon">
              <i className="icon_main activechecked"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.active_records}</p>
        </div>
        <div
          className={`bg_light_red details_card ${
            !filterObj.active && filterObj.archived ? "card_active" : ""
          }`}
          onClick={() => {
            setFilterObj({ active: false, archived: true })
            setPagination({ current_page: 1 })
            setLoading(true)
          }}
        >
          <div className="card_titleicon">
            <h3 className="card_title">Archive Users</h3>
            <span className="card_icon">
              <i className="icon_main archive_data"></i>
            </span>
          </div>
          <p className="tls_numbers">{recordsCount.archived_records}</p>
        </div>
      </div>

      <Collapse in={open}>
        <div id="brand-filter-collapse" className="filters_sticky">
          <Card className="mb-3">
            <Card.Body>
              <Stack direction="horizontal" gap={2} className="flex-wrap">
                <div className="d-flex gap-2 align-items-center justify-content-between flex-wrap">
                  <div className="d-inline-flex gap-2 align-items-center">
                    <SearchBox
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e?.target?.value)
                        setPagination({ current_page: 1 })
                        setLoading(true)
                      }}
                    />
                  </div>
                </div>
              </Stack>
            </Card.Body>
          </Card>
        </div>
      </Collapse>

      {!loading ? (
        <Card>
          <Card.Body>
            <InfiniteScroll
              dataLength={data.length}
              next={fetchMoreData}
              hasMore={hasMore}
              loader={
                <ManagePermissionsSkeleton infiniteScroll={true} rows={1} />
              }
            >
              <Table className="rounded mb-0 table-striped" responsive="md">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th className="text-center">Phone No.</th>
                    <th className="text-center">Role</th>
                    {(canEdit || canDelete) && (
                      <th className="text-center">Action</th>
                    )}
                  </tr>
                </thead>

                <tbody>
                  {data?.length > 0 &&
                    data?.map((data, index) => (
                      <tr key={index + 1}>
                        <td>{data.firstname + " " + data.lastname}</td>
                        <td>{data.email}</td>
                        <td className="text-center">{data.phone}</td>
                        <td className="text-center">
                          <Badge bg="primary">{data?.role ?? "-"}</Badge>
                        </td>
                        {(canEdit || canDelete) && (
                          <td className="text-center">
                            <div className="d-inline-flex gap-2">
                              {canEdit &&
                                !filterObj.archived &&
                                data?.deletedAt === null && (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Edit"
                                  >
                                    <Button
                                      variant="btnedit_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        handleShow()
                                        setIsUpdate(data)
                                      }}
                                    >
                                      <i className="icon_main edit_pencil"></i>
                                    </Button>
                                  </TooltipComponent>
                                )}
                              {canDelete &&
                                (data?.deletedAt === null ? (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Archive"
                                  >
                                    <Button
                                      variant="btnarchive_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(false)
                                        setShowAlert(true)
                                        setIsUpdate(data)
                                      }}
                                    >
                                      <i className="icon_main archive_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ) : (
                                  <TooltipComponent
                                    toolplace="top"
                                    tooltipText="Restore"
                                  >
                                    <Button
                                      variant="btnrestore_icon"
                                      className="btn_icon"
                                      onClick={() => {
                                        setArchived(true)
                                        setShowAlert(true)
                                        setIsUpdate(data)
                                      }}
                                    >
                                      <i className="icon_main restore_data"></i>
                                    </Button>
                                  </TooltipComponent>
                                ))}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  {data && !data.length && (
                    <tr>
                      <td className="text-center py-4" colSpan={6}>
                        <div className="norecord_wrapper">
                          <img src={recordNotFound} alt="No Record Found" />
                          <p className="norecords_title">No Records Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </InfiniteScroll>
          </Card.Body>
        </Card>
      ) : (
        <ManagePermissionsSkeleton />
      )}

      <AddEditUser
        show={show}
        handleClose={handleClose}
        fetchData={fetchData}
        setIsUpdate={setIsUpdate}
        isUpdate={isUpdate}
        setData={setData}
      />
      <SweetAlerts
        showAlert={showAlert}
        alertMessage={`Are you sure you want to ${
          archived ? "Restore" : "Archive"
        } this user ?`}
        handleClose={handleClose}
        iconClassName={`${
          archived ? "restore_data bg-black" : "archive_data bg-warning"
        }`}
        handleAlert={() => {
          handleArchive(isUpdate.id)
        }}
      />
    </>
  )
}

export default ManageUsers
